import { useState, useEffect, useMemo, useRef } from 'react';
import { cartService } from '@/features/cart/services/cartService';
import type { CartResponse, CartItemStatus } from '@/features/cart/types';
import { ItemStatusCode, CartStatus } from '@/features/cart/types';

export interface UseCartItemStatusesReturn {
  itemStatuses: Record<string, CartItemStatus | null>;
  setItemStatuses: React.Dispatch<React.SetStateAction<Record<string, CartItemStatus | null>>>;
}

export function useCartItemStatuses(cart: CartResponse | null): UseCartItemStatusesReturn {
  const [itemStatuses, setItemStatuses] = useState<Record<string, CartItemStatus | null>>({});
  const statusFetchingRef = useRef(false);

  // Memoize item IDs to create stable dependency
  const itemIdsString = useMemo(() => {
    if (!cart?.items || cart.items.length === 0) return '';
    return cart.items.map(item => item.id).sort().join(',');
  }, [cart?.items]);

  // Fetch statuses for all cart items asynchronously in batches
  // Only fetch if cart status is ACTIVE
  // For non-ACTIVE carts (except ABANDONED/REFUNDED), set green status without fetching
  useEffect(() => {
    const fetchItemStatuses = async () => {
      // Only fetch statuses if cart status is ACTIVE
      if (!cart || cart.status !== CartStatus.Active) {
        // For non-ACTIVE carts (except ABANDONED/REFUNDED), set green status
        if (cart && cart.status !== CartStatus.Abandoned && cart.status !== CartStatus.Refunded && cart.items) {
          const greenStatusMap: Record<string, CartItemStatus> = {};
          cart.items.forEach(item => {
            greenStatusMap[item.id] = {
              item_status_code: ItemStatusCode.GoodForBooking
            };
          });
          setItemStatuses(greenStatusMap);
        } else {
          setItemStatuses({});
        }
        statusFetchingRef.current = false;
        return;
      }

      if (!cart?.items || cart.items.length === 0) {
        setItemStatuses({});
        statusFetchingRef.current = false;
        return;
      }

      // Prevent duplicate fetches
      if (statusFetchingRef.current) return;
      statusFetchingRef.current = true;
      
      // Initialize all statuses as null to show loading state
      const initialStatusMap: Record<string, CartItemStatus | null> = {};
      cart.items.forEach(item => {
        initialStatusMap[item.id] = null;
      });
      setItemStatuses(initialStatusMap);

      try {
        // Process items in batches to avoid overwhelming the browser/server
        const BATCH_SIZE = 5;
        const items = cart.items;
        
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
          const batch = items.slice(i, i + BATCH_SIZE);
          
          // Fetch statuses for this batch
          const batchPromises = batch.map(async (item) => {
            try {
              const status = await cartService.getCartItemStatus(item.id);
              return { itemId: item.id, status: status.status, success: true };
            } catch (error) {
              console.error(`Failed to fetch status for item ${item.id}:`, error);
              return { itemId: item.id, status: null, success: false };
            }
          });

          // Use allSettled to handle individual failures gracefully
          const batchResults = await Promise.allSettled(batchPromises);
          
          // Update state incrementally as each batch completes
          setItemStatuses(prev => {
            const updated = { ...prev };
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled') {
                const { itemId, status } = result.value;
                updated[itemId] = status;
              } else {
                // Handle promise rejection (shouldn't happen due to try-catch, but just in case)
                console.error('Unexpected promise rejection:', result.reason);
              }
            });
            return updated;
          });

          // Small delay between batches to avoid overwhelming the server
          if (i + BATCH_SIZE < items.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      } catch (error) {
        console.error('Failed to fetch item statuses:', error);
      } finally {
        statusFetchingRef.current = false;
      }
    };

    fetchItemStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIdsString, cart?.status]); // Depend on cart status as well

  return {
    itemStatuses,
    setItemStatuses,
  };
}

